import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
} from '@mui/material';
import { PageHeader } from '../components/admin';
import { useMojeVybaveniQuery } from '../api/JsdhNotifikaceService/service/InventarMutations';
import dayjs from 'dayjs';
import 'dayjs/locale/cs';

dayjs.locale('cs');

const formatDate = (d?: string) => (d ? dayjs(d).format('DD.MM.YYYY') : '—');

const StavChip: React.FC<{ stav: string }> = ({ stav }) => {
    switch (stav) {
        case 'AKTIVNI':
            return <Chip label="Aktivní" color="success" size="small" />;
        case 'V_OPRAVE':
            return <Chip label="V opravě" color="warning" size="small" />;
        case 'VYRAZENO':
            return <Chip label="Vyřazeno" color="error" size="small" />;
        default:
            return <Chip label={stav} size="small" variant="outlined" />;
    }
};

const MojeVybaveniPage: React.FC = () => {
    const { data, isLoading, isError } = useMojeVybaveniQuery();

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError || !data) {
        return (
            <Box>
                <PageHeader title="Moje výstroj / OOP" />
                <Alert severity="error">Nepodařilo se načíst přidělené vybavení.</Alert>
            </Box>
        );
    }

    return (
        <Box>
            <PageHeader
                title={`Moje výstroj / OOP${data.uzivatelJmeno ? ` — ${data.uzivatelJmeno}` : ''}`}
            />

            {/* Aktivní přiřazení */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Přidělené vybavení
                    <Chip
                        label={data.pocetAktivnich}
                        color={data.pocetAktivnich > 0 ? 'primary' : 'default'}
                        size="small"
                        sx={{ ml: 1 }}
                    />
                </Typography>

                {data.aktivniPolozky.length === 0 ? (
                    <Typography color="text.secondary">Žádné přidělené vybavení.</Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Název</TableCell>
                                    <TableCell>Inv. číslo</TableCell>
                                    <TableCell>Velikost</TableCell>
                                    <TableCell>Stav</TableCell>
                                    <TableCell>Datum výdeje</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.aktivniPolozky.map((polozka) => (
                                    <TableRow key={polozka.id}>
                                        <TableCell>{polozka.nazev}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>
                                            {polozka.inventarniCislo ?? '—'}
                                        </TableCell>
                                        <TableCell>{polozka.velikost ?? '—'}</TableCell>
                                        <TableCell>
                                            <StavChip stav={polozka.stav} />
                                        </TableCell>
                                        <TableCell>
                                            {polozka.datumVydeje ? formatDate(polozka.datumVydeje) : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Historie přiřazení */}
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" gutterBottom>
                    Historie přiřazení
                    <Chip
                        label={data.pocetHistorickych}
                        size="small"
                        sx={{ ml: 1 }}
                    />
                </Typography>

                {data.historiePolozek.length === 0 ? (
                    <Typography color="text.secondary">Žádná historie přiřazení.</Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Položka</TableCell>
                                    <TableCell>Inv. číslo</TableCell>
                                    <TableCell>Vydáno</TableCell>
                                    <TableCell>Vráceno</TableCell>
                                    <TableCell>Stav vrácení</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.historiePolozek.map((prirazeni) => (
                                    <TableRow key={prirazeni.id}>
                                        <TableCell>{prirazeni.polozkaNazev ?? '—'}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>
                                            {prirazeni.polozkaInventarniCislo ?? '—'}
                                        </TableCell>
                                        <TableCell>{formatDate(prirazeni.datumVydeje)}</TableCell>
                                        <TableCell>
                                            {prirazeni.datumVraceni ? (
                                                formatDate(prirazeni.datumVraceni)
                                            ) : (
                                                <Chip label="Aktivní" color="info" size="small" />
                                            )}
                                        </TableCell>
                                        <TableCell>{prirazeni.stavPriVraceni ?? '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
};

export default MojeVybaveniPage;
